import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingForm = () => {
  const { lang, copy } = useLanguage();
  const c = copy.contact;
  const b = copy.booking;
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "", mode: "online", notes: "", privacy: false });
  const [sending, setSending] = useState(false);
  const [slots, setSlots] = useState(null);

  const loadSlots = (date) => {
    setSlots(null);
    return axios.get(`${API}/availability/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  };

  useEffect(() => {
    if (form.date) loadSlots(form.date);
  }, [form.date]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value, ...(k === "date" ? { time: "" } : {}) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy || !form.time) return;
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        date: form.date,
        time: form.time,
        mode: form.mode,
        notes: form.notes || null,
        lang,
      });
      toast.success(b.success);
      setForm({ name: "", email: "", phone: "", date: "", time: "", mode: "online", notes: "", privacy: false });
      setSlots(null);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(b.taken);
        setForm((f) => ({ ...f, time: "" }));
        loadSlots(form.date);
      } else {
        toast.error(b.error);
      }
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-ink placeholder:text-muted/60 outline-none transition-colors duration-300 focus:border-pine focus:ring-2 focus:ring-pine/15";
  const labelCls = "mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="bk-name" className={labelCls}>{c.name} *</label>
          <input id="bk-name" data-testid="booking-form-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder={c.name} />
        </div>
        <div>
          <label htmlFor="bk-email" className={labelCls}>{c.email} *</label>
          <input id="bk-email" data-testid="booking-form-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="tu@email.com" />
        </div>
      </div>
      <div>
        <label htmlFor="bk-phone" className={labelCls}>{c.phone}</label>
        <input id="bk-phone" data-testid="booking-form-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+34 ..." />
      </div>
      <div>
        <label htmlFor="bk-date" className={labelCls}>{b.date} *</label>
        <input id="bk-date" data-testid="booking-form-date" type="date" required min={tomorrow} value={form.date} onChange={set("date")} className={inputCls} />
      </div>
      <div>
        <span className={labelCls}>{b.time} *</span>
        {!form.date ? (
          <p className="text-sm text-muted/80" data-testid="booking-pick-date-hint">{b.pickDateFirst}</p>
        ) : slots === null ? (
          <p className="text-sm text-muted/80" data-testid="booking-slots-loading">{b.loadingSlots}</p>
        ) : slots.length === 0 ? (
          <p className="text-sm text-clay-dark" data-testid="booking-no-slots">{b.noSlots}</p>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="booking-form-slots">
            {slots.map(({ time, available }) => (
              <button
                type="button"
                key={time}
                disabled={!available}
                data-testid={`booking-slot-${time.replace(":", "")}`}
                onClick={() => setForm({ ...form, time })}
                className={`rounded-full border px-3 py-2.5 font-mono text-sm transition-colors duration-300 disabled:cursor-not-allowed disabled:line-through disabled:opacity-40 ${
                  form.time === time ? "border-pine bg-pine text-linen" : "border-border bg-white text-ink/70 hover:border-pine/50"
                }`}
              >
                {time}
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <span className={labelCls}>{c.mode}</span>
        <div className="flex gap-3" data-testid="booking-form-mode">
          {[["online", c.modeOnline], ["presencial", c.modePresential]].map(([val, label]) => (
            <button
              type="button"
              key={val}
              data-testid={`booking-mode-${val}`}
              onClick={() => setForm({ ...form, mode: val })}
              className={`flex-1 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                form.mode === val ? "border-pine bg-pine text-linen" : "border-border bg-white text-ink/70 hover:border-pine/50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label htmlFor="bk-notes" className={labelCls}>{b.notes}</label>
        <textarea id="bk-notes" data-testid="booking-form-notes" rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} />
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="booking-form-privacy-label">
        <input type="checkbox" data-testid="booking-form-privacy" required checked={form.privacy} onChange={set("privacy")} className="mt-1 h-4 w-4 accent-pine" />
        <span className="text-xs text-muted leading-relaxed">{c.privacy}</span>
      </label>
      <button
        type="submit"
        data-testid="booking-form-submit"
        disabled={sending || !form.privacy || !form.time}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-clay px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-clay-dark hover:shadow-lg disabled:opacity-50"
      >
        <CalendarCheck size={16} /> {sending ? b.sending : b.submit}
      </button>
    </form>
  );
};
